import RenderManager from "../../rendering/render-manager";
import * as States from "./helpers/helper-states";
import * as LabelData from "./helpers/mesh-label-data";
import BoxIntersect from "box-intersect";
import Utils from "../../utils";
import Input from "../../interactivity/inputs";
import { fire } from "../../utils/events";
import SlideManager from "../../utils/slide-manager";
import LayersManager from "../../layers/LayersManager";
import ensureArray from '../../utils/ensure-array';
import { MAX_DISTANCE } from "../../rendering/glraycaster";
import AssetLibrary from "../../assets/asset-library";
import checkObjectVisibility from "../../utils/is-visible";

import MaxDistanceLayout from './layout/maxDistanceLayout';
import LayoutLabelActivator from './layout/layoutLabelActivator';
import AssessmentManager from "../../assessment/assessment-manager";

const MAX_PIXELS = 12;
const MIN_PIXEL_THRESHOLD = 0.02;
const SUPPORTED_VERSIONS = ["1.1"];
const WARN_MESSAGE = "Label is potentially out of bounds. Move the camera closer and reset livelabel sizes if labels fail to render properly.";

let _tmpElements;
if (Utils.queryParams.has("draw_2d_collisions"))
    _tmpElements = [];

const g_vec = new THREE.Vector2();
const g_point = new THREE.Vector3();
const g_box3 = new THREE.Box3();
const g_center = new THREE.Vector3();

class LivelabelManager {
    constructor({ configs }) {
        this.configs = configs;
        this.LabelVisibility_T = LabelData.LabelVisibility_T;

        this._modifyState = new States.ModifyState();
        this._drawState = new States.DrawState();
        this._highlightState = new States.HighlightState();
        this._regionState = new States.RegionState();
        this._lastState = new States.LastState();
        this._calloutState = new States.CalloutState();
        this._renderingState = new States.RenderingState();

        /**
         * @type {LabelData.MeshLabel[]}
         */
        this._meshLabels = {};

        this._lastRaycasterRevision = {};
        this._labelLayout = new MaxDistanceLayout();
        this._layoutLabelActivator = new LayoutLabelActivator();
        this._layoutLabelActivator.setLayout(this._labelLayout);

        Object.freeze(this);
    }

    get language() { return this._language; }
    set language(val) { this._language = val; }

    clear() {
        Object.keys(this._meshLabels)
            .forEach(key => delete this._meshLabels[key]);
    }

    clearLast() {
        if (Utils.queryParams.has("keep_label_position")) return;

        const isDamping = Q3.controls && Q3.controls.isDamping();

        if (isDamping) return;

        const raycaster = RenderManager.raycaster;
        const modType = this._modifyState.type;
        const vec = g_vec.set(0, 0);

        const minPixels = Math.min(Math.floor(MIN_PIXEL_THRESHOLD *
            Math.min(RenderManager.raycaster.width, RenderManager.raycaster.height)
        ), MAX_PIXELS) * Math.min(RenderManager.pixelRatio, 1);


        const slideCallouts = SlideManager.currentSlide.useCallouts === undefined
            ? -1
            : SlideManager.currentSlide.useCallouts;
        const isCallout = slideCallouts === -1 ? Q3.data.setups.useCallouts : !!slideCallouts;

        let labelsLerping = false;

        Object.keys(this._meshLabels).forEach(key => {
            const v = this._meshLabels[key];
            const mesh = v._object;
            const regions = raycaster.getRegions(mesh);

            v._labels.forEach(label => {
                const region = regions[label._index];

                if (!region || !label._isValid) return;
                const regionId = region.id;

                const activeMesh = this._drawState.mesh ? this._drawState.mesh.parent : null;
                if (modType !== States.MODIFY_TYPE.MODIFY_NONE && activeMesh && region.subId >= 0)
                    return;

                /**
                 * @type {LabelState}
                 */
                const state = label._state;

                if (state.lerping) {
                    labelsLerping = true;
                    return;
                }

                const labelPos = this._labelLayout.getLabel(regionId);
                const point = labelPos ? labelPos.pointPosition : null;

                if (point) {
                    const mask = raycaster.getMask();
                    const { width, height } = mask;
                    vec.set(point.x / width * 2 - 1, point.y / height * 2 - 1);
                    const ray = raycaster.pick(vec);
                    if (ray && ray.regionId === regionId && !state.visible) {
                        state.positionDesired.copy(ray.local);
                        state.rayValid.copy(vec);
                        state.depth = ray.depth;

                        state.lerp = 0;
                        state.lerping = true;
                        labelsLerping = true;
                    }
                }

            })
        });

        this._renderingState.labelsLerping = labelsLerping;
    }

    createMissingLabels() {
        Object
            .keys(Q3.data.assets.objects)
            .forEach(uuid => {
                const object = Q3.data.assets.objects[uuid];

                if (!object) return;

                const type = object.type;

                if (type !== "Mesh" && type !== "SkinnedMesh") return;

                const mesh = AssetLibrary.objects[uuid];
                const hasLabel = this._meshLabels[mesh.id];

                if (hasLabel) return;

                const meshLabel = this._tryGetMeshLabel(mesh);
                const liveLabel = meshLabel._labels[0];
                const cleanName = (name) => {
                    const nonEducationalWords = ["Box", "Object", "Mesh", "Base", "Plane", "Text", "Ground", "Dummy", "Cylinder", "Group", "box", "Band", "Geosphere", "Sphere", "Line", "Stand", "Rectangle", "Tube", "Helix", "Quad", "Circle", "Cone", "Arc"];
                    return name.replace(/_|\d|\./g, " ").replace(new RegExp(nonEducationalWords.join("|"), "gi"), " ").replace(/\s\s+/g, " ").trim()
                }
                liveLabel.element.content = cleanName(object.name);
                this.initMeshLabel(mesh);

                this.resetClippingDistance(mesh, liveLabel, { region: 0 });
            });
    }

    get labelsLerping() {
        return this._renderingState.labelsLerping;
    }

    get labelsTransitioning() {
        return this._renderingState.labelsTransitioning;
    }

    updateTransition() {
        let labelsTransitioning = false;

        Object.keys(this._meshLabels).forEach(key => {
            const v = this._meshLabels[key];

            v._labels.forEach(xlabel => {
                /**
                 * @type {LiveLabel}
                 */
                const label = xlabel;

                if (label) {
                    label._element.updateTransition();

                    if (label._element.isTransitioning) {
                        labelsTransitioning = true;
                    }
                }
            });
        });

        this._renderingState.labelsTransitioning = labelsTransitioning;
    }

    update(force = false) {
        if (Utils.queryParams.has("disable_labels")) {
            Object.keys(this._meshLabels).forEach(key => {
                const v = this._meshLabels[key];

                v._labels.forEach(xlabel => {
                    /**
                     * @type {LiveLabel}
                     */
                    const label = xlabel;

                    label._element.fadeout()
                    label._testObject.visible = false;
                })
            });
            return;
        }

        if ((RenderManager.info.render.frame < 30 || this._renderingState.locked)
            && !force || Q3.isTransitioning) return;

        const slideCallouts = SlideManager.currentSlide.useCallouts === undefined
            ? -1
            : SlideManager.currentSlide.useCallouts;
        const isCallouts = slideCallouts === -1 ? Q3.data.setups.useCallouts : !!slideCallouts;

        if (isCallouts) { this._renderCallouts(); }
        else { this._renderLabels(); }
    }

    _renderLabels() {
        const labels = this.computeLabelValidty();
        const slideQuiz = SlideManager.currentSlide.slideQuiz;

        // const vp = RenderManager.getViewPort(),
        //     vpWidth = vp.right - vp.left,
        //     vpHeight = vp.bottom - vp.top;

        const { width: vpWidth, height: vpHeight } = RenderManager.getSize();
        // const vpLeft = 0, vpTop = 0;

        labels.valid.forEach(possible => {
            const label = possible.label;

            label._testObject.visible = true;

            label._element.setXYZ(possible.point);
            label._element.render(vpWidth, vpHeight);

            label._element.fadein();
            label._element.setDynamic(Input.mouse.isDown);

            if (Utils.queryParams.has("no_labels_2d")) {
                label._element.hide();
                label._element.fadeout();
            }
        });

        labels.invalid.forEach(invalid => {
            const label = invalid.label;

            label._element.fadeout()
            label._testObject.visible = false;

            if (!Q3.editMode && slideQuiz && slideQuiz.type === "draglabels") {
                label._state.visible = true;
                label._state.obstructedBy2d = false;
                label._element.setXYZ(invalid.point);
                label._element.render(vpWidth, vpHeight);
            }
        });
    }

    computeLabelValidty() {
        const raycaster = RenderManager.raycaster;

        const bufferUpdated = this._lastRaycasterRevision.revision !== raycaster.revision;
        if (bufferUpdated) {
            this._lastRaycasterRevision.revision = raycaster.revision;
            const mask = raycaster.getMask();
            this._labelLayout.setMask(mask);
        }
        this._labelLayout.update(bufferUpdated);

        const visibleLabels = [];
        const { potential: potentialLabels, invisible: invisibleLabels } = this._computePotential(false);

        const boxes = potentialLabels.map(({ bbox }) => {
            return [bbox.min.x, bbox.min.y, bbox.max.x, bbox.max.y];
        });

        let w = RenderManager.domElement.width / window.devicePixelRatio;
        let h = RenderManager.domElement.height / window.devicePixelRatio;

        collectBoxes(boxes, w, h);

        if (Utils.queryParams.has("draw_2d_collisions")) {
            let el;
            while (el = _tmpElements.pop()) {
                el.remove();
            }

            boxes.forEach(box => {
                const width = box[2] - box[0];
                const height = box[3] - box[1];
                let left = box[0];
                let top = box[1];

                const div = document.createElement("div");

                div.style.position = "absolute";
                div.style.background = "rgba(255, 0, 0, 0.5)";
                div.style.left = left + "px";
                div.style.top = top + "px";
                div.style.width = width + "px";
                div.style.height = height + "px";

                RenderManager.overlay2d.appendChild(div);

                _tmpElements.push(div);
            });
        }

        const filteredOut = filterBoxes(boxes);

        potentialLabels.forEach((label, i) => {
            let valid = false;

            if (filteredOut[i] === undefined && label.label?.isTurnOn !== false) {
                visibleLabels.push(label);
                label.label._state.rayValid.copy(label.label._state.rayCurrent);
                valid = true;
            } else { invisibleLabels.push(label); }

            label.label._state.visible = valid;
            label.label._state.obstructedBy2d = !valid;
        });

        return { valid: visibleLabels, invalid: invisibleLabels };
    }

    setGlobalVisibility(type) {
        const slide = SlideManager.currentSlide;
        switch (type) {
            case "hidden":
                slide.visibilityType = this.LabelVisibility_T.HIDDEN;
                slide.useCallouts = false;
                break;
            case "on_demand":
                slide.visibilityType = this.LabelVisibility_T.ON_DEMAND;
                slide.useCallouts = false;
                break;
            case "visible":
                slide.visibilityType = this.LabelVisibility_T.ALWAYS;
                slide.useCallouts = false;
                break;
            case "callouts":
                slide.visibilityType = this.LabelVisibility_T.ALWAYS;
                slide.useCallouts = true;
                break;
            default:
                throw new Error(`Unknown type: ${type}`);
        }
    }

    _computePotential(isCallout) {
        /**
         * @type {THREE.Camera}
         */
        const camera = RenderManager.activeCamera;
        const raycaster = RenderManager.raycaster;
        const vec = g_vec.set(0, 0);
        const point = g_point.set(0, 0, 0);
        const slideQuiz = SlideManager.currentSlide.slideQuiz;

        /**
         * @type {PotentialLabel[]}
         */
        const potentialLabels = [];

        const vp = RenderManager.getViewPort();
        const vpWidth = vp.right - vp.left;
        const vpHeight = vp.bottom - vp.top;
        const vpLeft = vp.left, vpTop = vp.top;

        // const { width: vpWidth, height: vpHeight } = RenderManager.getSize();
        // const vpLeft = 0, vpTop = 0;

        const minPixels = Math.min(Math.floor(MIN_PIXEL_THRESHOLD *
            Math.min(RenderManager.raycaster.width, RenderManager.raycaster.height)
        ), MAX_PIXELS);

        const activeMesh = this._drawState.mesh ? this._drawState.mesh.parent : null;
        const modType = this._modifyState.type;
        const invisibleLabels = [];

        let x, y, z;

        let labelsLerping = false;

        Object.keys(this._meshLabels).forEach(key => {
            const v = this._meshLabels[key];
            const mesh = v._object;

            const regions = raycaster.getRegions(mesh);

            v._labels.forEach(xlabel => {
                /**
                 * @type {LiveLabel}
                 */
                const label = xlabel;

                if (!label) return;

                const region = regions[label._index];
                const objectIsVisible = checkObjectVisibility(label.object);
                const slideAnim = SlideManager.currentSlide.animation[xlabel.uuid];
                const visibleInSlide = (!slideAnim || slideAnim.visible !== false) && objectIsVisible;

                if (!region || !label._isValid) return;
                if (modType !== States.MODIFY_TYPE.MODIFY_NONE && activeMesh && region.subId >= 0) {
                    const sub = label.object.subRegions.children[region.subId];

                    if (activeMesh === sub) {
                        label._element.fadeout();
                        return;
                    }
                }

                const regionId = region.id;
                let isVisible = false;

                // label._element.hide();
                // label._element.show();

                const state = label._state;
                const wasVisible = state.visible && !state.forcedVisible;
                const obstructedBy2d = state.obstructedBy2d;
                let ray;

                // const visibilityType = label._element.visibilityType;
                const visibilityType = SlideManager.currentSlide.visibilityType || LabelData.LabelVisibility_T.ALWAYS;
                const labelVisibilityAllowed =
                    !Q3.transformControls.visible && !AssessmentManager.isAssessmentActive && (
                        visibilityType === LabelData.LabelVisibility_T.ALWAYS ||
                        (visibilityType === LabelData.LabelVisibility_T.HOVER && label.isHighlighted) ||
                        (visibilityType === LabelData.LabelVisibility_T.ON_DEMAND && label.isDemandLabel)
                    );

                if (regionId > 0 && labelVisibilityAllowed && visibleInSlide) {
                    if (!wasVisible && !obstructedBy2d) {
                        const labelPos = this._labelLayout.getLabel(regionId);
                        const point = labelPos ? labelPos.pointPosition : null;

                        if (point) {
                            const mask = raycaster.getMask();
                            const { width, height } = mask;

                            vec.set(point.x / width * 2 - 1, point.y / height * 2 - 1);
                            ray = raycaster.pick(vec, undefined, "cyan");

                            if (ray && ray.regionId === regionId) {
                                state.positionOriginal.copy(ray.local);
                                state.positionDesired.copy(ray.local);
                                state.depth = ray.depth;
                                state.lerp = 1;
                                state.lerping = false;

                                isVisible = true;
                            }
                        }
                    } else {
                        vec.copy(state.rayValid);
                        ray = raycaster.pick(vec, undefined, "red");
                        if (ray && ray.regionId === regionId) {

                            state.depth = ray.depth;
                            isVisible = true;
                        } else {
                            const labelPos = this._labelLayout.getLabel(regionId);
                            const point = labelPos ? labelPos.pointPosition : null;

                            if (point) {
                                const mask = raycaster.getMask();
                                const { width, height } = mask;

                                vec.set(point.x / width * 2 - 1, point.y / height * 2 - 1);
                                ray = raycaster.pick(vec, undefined, "green");

                                if (ray && ray.regionId === regionId) {

                                    isVisible = true;
                                    state.depth = ray.depth;
                                    state.positionOriginal.copy(ray.local);
                                    state.positionDesired.copy(ray.local);
                                    state.depth = ray.depth;
                                    state.lerp = 1;
                                    state.lerping = false;
                                }
                            }
                        }

                        // if (isVisible && (!wasVisible || obstructedBy2d) && !state.lerping) {
                        //     state.positionDesired.copy(ray.local);
                        //     state.rayValid.copy(vec);
                        //     state.depth = ray.depth;

                        //     state.lerp = 0;
                        //     state.lerping = true;
                        // }
                    }

                    if (isVisible && ray.parent) {
                        point
                            .lerpVectors(state.positionOriginal, state.positionDesired, state.lerp)
                            .applyMatrix4(ray.parent.matrixWorld);

                        label._testObject.position.copy(point);

                        point.project(camera);

                        label._position.copy(point);

                        // if (point.x < -1 || point.y < -1 || point.x > 1 || point.y > 1)
                        //     debugger;

                        state.lerp += 0.25;

                        if (state.lerp >= 1) {
                            state.lerp = 1;
                            state.lerping = false;
                            state.positionOriginal.copy(state.positionDesired);
                        }

                        ({ x, y } = RenderManager.toScreenCoords(point.x, point.y));
                        z = state.depth;
                    }
                }

                state.forcedVisible = false;
                if (!Q3.editMode && slideQuiz && slideQuiz.type === "draglabels" && !isVisible && objectIsVisible) {
                    const center = g_box3
                        .setFromObject(label.object)
                        .getCenter(g_center);

                    z = center.distanceTo(camera.position);

                    const point = center.project(camera);

                    // if (point.x < -1 || point.y < -1 || point.x > 1 || point.y < 1)
                    //     debugger;

                    ({ x, y } = RenderManager.toScreenCoords(point.x, point.y));

                    isVisible = true;
                    state.lerp = 1;
                    state.lerping = false;
                    // state.forcedVisible = true;
                }

                const xyz = new THREE.Vector3(x, y, z);

                if (x < 0 || y < 0 || x > vpWidth || y > vpHeight) isVisible = false;

                /**
                 * @type {DOMRect}
                 */
                const bbox = isVisible && label._element.getApproximateBoundingBox(vpWidth, vpHeight, isCallout, xyz);

                if (isVisible && bbox.width > 0 && bbox.height > 0 && !Q3.selectingMesh && !Q3.initializingQuiz) {
                    if (false && state.lerping) {
                        invisibleLabels.push(label);
                    } else {
                        potentialLabels.push(new LabelData.PotentialLabel({
                            label,
                            bbox: new THREE.Box2(
                                new THREE.Vector2(bbox.left, bbox.top),
                                new THREE.Vector2(bbox.right, bbox.bottom),
                            ),
                            regionId,
                            point: xyz
                        }));
                        state.rayCurrent.copy(point);

                        if (state.lerping) {
                            labelsLerping = true;
                        }
                    }
                } else {
                    // label._element.fadeout();
                    invisibleLabels.push(new LabelData.PotentialLabel({
                        label,
                        bbox: new THREE.Box2(
                            new THREE.Vector2(bbox.left, bbox.top),
                            new THREE.Vector2(bbox.right, bbox.bottom),
                        ),
                        regionId,
                        point: xyz
                    }));
                    state.visible = false;
                    state.obstructedBy2d = false;
                }
            });
        });

        this._renderingState.labelsLerping = labelsLerping;

        return { potential: potentialLabels, invisible: invisibleLabels };
    }

    _tryGetMeshLabel(object) { return this._meshLabels[object.id] = this._meshLabels[object.id] || new LabelData.MeshLabel(object); }


    _renderCallouts() {
        const labels = this.computeCallouts();
        const { width: vpWidth, height: vpHeight } = RenderManager.getSize();

        const boxes = [];
        collectBoxes(boxes, vpWidth, vpHeight);

        /**
         *
         * @param {PotentialLabel} potential
         * @param {string} transform
         */
        function renderLabel(potential, transform) {
            const label = potential.label;
            const dom = label._element;
            const { x, y } = potential.point;
            const _x = potential.calloutData.lx, _y = potential.calloutData.ly;

            potential.point.x = _x, potential.point.y = _y;

            const state = label._state;

            if (!state.visible) dom.setXYZ(potential.point);
            else dom.setDesiredXYZ(potential.point);

            const copyBoxes = boxes.slice();
            const bbox = dom.getApproximateBoundingBox(vpWidth, vpHeight, true, { x: _x, y: _y, z: dom.z });
            copyBoxes.push([bbox.left - bbox.width * 0.5, bbox.top, bbox.right - bbox.width * 0.5, bbox.bottom]);

            const filteredOut = filterBoxes(copyBoxes);

            if (filteredOut[copyBoxes.length - 1] !== undefined) {
                dom.hide();
                dom.fadeout();
                state.visible = false;
                state.obstructedBy2d = true;
                label._testObject.visible = false;
            } else {
                state.visible = true;
                state.obstructedBy2d = false;
                state.rayValid.copy(state.rayCurrent);

                dom.setCalloutPath(_x, _y, x, y);

                dom.show();
                dom.fadein();
                dom.render(vpWidth, vpHeight, true);


                dom.setDynamic(Input.mouse.isDown);

                // dom.domElement.style.transform = transform;
                if (transform === "right") {
                    if (dom.domElement.classList.contains("left")) {
                        dom.domElement.classList.remove("left");
                    }
                    if (!dom.domElement.classList.contains("right")) {
                        dom.domElement.classList.add("right")
                    }
                } else if (transform === "left") {
                    if (dom.domElement.classList.contains("right")) {
                        dom.domElement.classList.remove("right");
                    }
                    if (!dom.domElement.classList.contains("left")) {
                        dom.domElement.classList.add("left")
                    }
                }

                label._testObject.visible = true;
            }
        }

        let labelsTransitioning = false;

        labels.left.forEach(data => {
            renderLabel(data, "left");
            // renderLabel(data, "translate(-100%, -50%)");
            data.label.element.side = "left";

            if (data.label.element.isTransitioning) {
                labelsTransitioning = true;
            }
        });
        labels.right.forEach(data => {
            renderLabel(data, "right");
            // renderLabel(data, "translate(0%, -50%)");
            data.label.element.side = "right";

            if (data.label.element.isTransitioning) {
                labelsTransitioning = true;
            }
        });

        labels.invalid.forEach(invalid => {
            const label = invalid.label;

            label._state.visible = false;

            label._element.fadeout();
            label._testObject.visible = false;
        });

        this._renderingState.labelsTransitioning = labelsTransitioning;
    }


    computeCallouts() {

        const raycaster = RenderManager.raycaster;

        const bufferUpdated = this._lastRaycasterRevision.revision !== raycaster.revision;
        if (bufferUpdated) {
            this._lastRaycasterRevision.revision = raycaster.revision;
            const mask = raycaster.getMask();
            this._labelLayout.setMask(mask);
        }
        this._labelLayout.update(bufferUpdated);

        const { potential: potentialLabels, invisible: invisibleLabels } = this._computePotential(true);

        // const selectedIndex = Q3.selected instanceof LiveLabel ? invisibleLabels.indexOf(Q3.selected) : -1;
        // if (selectedIndex > -1) {
        //     potentialLabels.push(Q3.selected);
        //     invisibleLabels.splice(selectedIndex, 1);
        // }

        const visibleRight = [], visibleLeft = [];
        const { width: vpWidth, height: vpHeight } = RenderManager.getSize();

        potentialLabels.sort((a, b) => b.point.x - a.point.x);

        const lxMargin = 0.2 * vpWidth;

        const lyMargin1 = 0.2;
        const lyMargin2 = (1 - 2 * lyMargin1)

        // we are going to have space for roughly 2 * ly_margin2 * HEIGHT / average label height
        // remove labels if there are too many of them

        const maxLabels = 2 * lyMargin2 * vpHeight / 30;

        if (maxLabels > 2) {
            while (potentialLabels.length > maxLabels) {
                // Hide invalid labels
                let index = Math.floor(potentialLabels.length / 2);

                if (potentialLabels[index].isHighlighted) index -= 1;

                const label = potentialLabels.splice(index, 1)[0];
                invisibleLabels.push(label);
            }
        }

        const n = potentialLabels.length;
        const n2 = Math.floor(potentialLabels.length * 0.5);
        let lyDenominator = Math.max(1, n2 - 1);

        for (let i = 0; i < n2; i++) {
            // right side
            const lx = vpWidth - lxMargin;
            const ly = vpHeight * (lyMargin1 + lyMargin2 * i / lyDenominator);

            // find the label best shown for this spot
            let best = -Infinity;
            let index = 0;

            for (let k = 0; k < n2; k++) {
                const label = potentialLabels[k];

                if (label.calloutData.index < 0) {
                    const dx = lx - label.point.x;
                    const dy = ly - label.point.y;

                    const a = Math.atan2(dy, dx);
                    if (a > best) {
                        best = a;
                        index = k;
                    }
                }
            }

            const label = potentialLabels[index];
            label.calloutData.index = index;
            label.calloutData.lx = lx;
            label.calloutData.ly = ly;

            if (label.label?.isTurnOn !== false) visibleRight.push(label);
            else invisibleLabels.push(label);
        }

        lyDenominator = Math.max(1, n - 1 - n2);
        for (let i = n2; i < n; i++) {
            // left side
            const lx = lxMargin;
            const ly = vpHeight * (lyMargin1 + lyMargin2 * (1 - (i - n2) / lyDenominator));

            // find the label best shown for this spot
            let best = -Infinity;
            let index = 0;
            for (let k = n2; k < n; k++) {
                const label = potentialLabels[k];
                if (label.calloutData.index < 0) {
                    const dx = label.point.x - lx;
                    const dy = label.point.y - ly;
                    const a = Math.atan2(dy, dx);
                    if (a > best) {
                        best = a
                        index = k
                    }
                }
            }

            const label = potentialLabels[index];
            label.calloutData.index = index
            label.calloutData.lx = lx;
            label.calloutData.ly = ly;

            if (label.label?.isTurnOn !== false) visibleLeft.push(label);
            else invisibleLabels.push(label);
        }

        return { left: visibleLeft, right: visibleRight, invalid: invisibleLabels };
    }

    get isLocked() { return this._renderingState.locked; }

    get regionOverlay() { return this._regionState.overlay; }
    get selectedRegion() { return this._regionState.selected; }
    get isDisplayingRegions() { return this._regionState.isDisplaying; }
    get isDrawingRegion() { return this._modifyState.type !== States.MODIFY_TYPE.MODIFY_NONE; }

    get highlighted() { return this._highlightState.highlighted; }

    setLocked(isLocked) {
        if (this._renderingState.locked !== isLocked) {
            this.clearLast();
            Object.keys(this._meshLabels).forEach(key => {
                /**
                 * @type {MeshLabel}
                 */
                const meshLabel = this._meshLabels[key];

                meshLabel._labels.forEach(label => {
                    if (label && label._element)
                        label._element.fadeout();
                });
            });
        }
        this._renderingState.locked = isLocked;
    }

    setSelectedRegion(object, idxRegion) {
        const meshLabel = this._meshLabels[object.id];
        if (!meshLabel) return;

        const region = meshLabel._labels[idxRegion];
        if (!region) return;

        this._regionState.selected = region.getDTO({
            regionCount: meshLabel._labels.length,
            activeRegion: idxRegion
        });

        this._regionState.isDisplaying = true;
    }

    clearSelectedRegion() {
        this._regionState.selected = null;
        this._regionState.isDisplaying = false;
        RenderManager.clearOverlay(this._regionState.overlay);
    }

    /**
     *
     * @param {THREE.Mesh} object
     */
    getMeshLabel(object) {
        if (!object) return;

        /**
         * @type {LabelData.MeshLabel}
         */
        let meshLabel = this._meshLabels[object.id];

        if (meshLabel === null) return null;
        else if (meshLabel === undefined) {
            this._meshLabels[object.id] = null;
            meshLabel = this._meshLabels[object.id] = new LabelData.MeshLabel(object);
        }

        return meshLabel._getActiveLabel().getDTO({
            regionCount: meshLabel._labels.length,
            activeRegion: meshLabel._activeRegion
        });
    }

    selectRegion(object, index) {
        this.setSelectedRegion(object, index);
        this.renderRegions(RenderManager.activeCamera, object);
        this.update();
    }

    getLabelByUuid(object, uuid) {
        if (arguments.length === 1) {
            uuid = object;

            for (let key in this._meshLabels) {
                const meshLabel = this._meshLabels[key];
                const found = meshLabel._labels.find(x => x.uuid === uuid || x.object.uuid === uuid);

                if (found) return found;
            }

            return null;
        }

        const meshLabel = this._meshLabels[object.id];

        if (!meshLabel) return null;

        return meshLabel._labels.find(x => x.uuid === uuid || x.object.uuid === uuid);
    }
    getAllLabelTexts() {
        return Object.values(this._meshLabels).reduce((acc, meshLabel) => {
            acc = [...acc, ...(meshLabel._labels
                .filter(label => label.isInitialized && label._isValid)
                .map(label => label.name))];
            return acc;
        }, []);
    }

    getAllLabels(object) {
        const getLabelData = object => {
            const meshLabel = this._meshLabels[object.id];

            if (!meshLabel) return [];

            return meshLabel._labels
                .filter(label => label && label.isInitialized && label._isValid)
                .map(label => label.getDTO({}));
        };

        return object
            ? getLabelData(object)
            : Object.values(this._meshLabels).map(({ _object: object }) => getLabelData(object));
    }

    /**
     *
     * @param {THREE.Mesh} mesh
     * @param {THREE.Raycaster} raycaster
     * @param {THREE.Camera} camera
     * @param {THREE.Vector2} position
     * @param {THREE.Mesh}
     */
    intersectRegion(mesh, raycaster, camera, position) {
        const webGLRaycaster = RenderManager.raycaster;
        const webGLIntersection = webGLRaycaster.pick(position, camera);

        if (!webGLIntersection || webGLIntersection.objectId !== mesh.id) return null;

        const localRegion = webGLRaycaster.getRegionLocal(mesh.id, webGLIntersection.regionId);

        if (!localRegion || localRegion.subId < 0) return null;

        const intersections = raycaster.intersectObject(mesh.subRegions.children[localRegion.subId], true);

        return intersections[0] || null;
    }


    /**
     *
     * @param {THREE.Mesh} object
     */
    addRegion(object) {
        const meshLabel = this._tryGetMeshLabel(object);

        RenderManager.raycaster.addRegion(object);

        const label = meshLabel._addRegion();

        meshLabel._activeRegion = label._index;

        {
            const regionId = RenderManager.raycaster.getRegions(object)[label._index].id;
            this._layoutLabelActivator.enableLabel(regionId, true);
        }

        setTimeout(() => {
            const el = meshLabel._labels[0].element;
            if (el._input) el._input.focus();
        }, 500);

        return label.getDTO({
            regionCount: meshLabel._labels.length,
            activeRegion: meshLabel._activeRegion
        });
    }

    hasMeshLabel(object) { return this._meshLabels[object.id] && this._meshLabels[object.id]._labels[0]._isValid; }

    alert() {
        if (Q3.assetMode !== "view") {
            console.warn(WARN_MESSAGE);
        } else {
            console.warn(WARN_MESSAGE);
            let controls = document.getElementById("asset-viewer-label-controls");
            if (controls) {
                controls.style.display = "none";
            }
        }
    }

    initMeshLabel(object, intersection) {

        const regionId = RenderManager.raycaster.getRegions(object)[0].id;

        if (!intersection) {
            const minDist = RenderManager.raycaster.longestRows.min[regionId];
            const maxDist = RenderManager.raycaster.longestRows.max[regionId];

            intersection = { distance: (maxDist + minDist) * 0.5 };
        }

        const meshLabel = this._tryGetMeshLabel(object);
        const label = meshLabel._labels[0];

        this._layoutLabelActivator.enableLabel(regionId, true);

        label._isValid = true;
        label._instantiated = true;
        label.element.domElement.style.removeProperty("display")

        setTimeout(function () { if (label.element._input) label.element._input.focus(); }, 500);

        label._element.setBestDistance(intersection.distance);

        if (MAX_DISTANCE * 0.75 <= intersection.distance + this.configs.clip_equation(intersection.distance)) this.alert();

        RenderManager.enqueRaycaster();

        this.selectRegion(object, 0);

        this.update();

        Q3.selected = label;
        fire("selectchange");

        return label;
    }

    deleteMeshLabel(label) {
        Q3.Events.fire("delete_label", { labelUuid: label.uuid });
        delete Q3.data.quizData[label.uuid];

        let index = label.index;

        {
            const regionId = RenderManager.raycaster.getRegions(label.object)[index].id;
            this._layoutLabelActivator.enableLabel(regionId, false);
        }

        label._isValid = false;
        label._instantiated = false;
        const el = label.element;
        if (index !== 0) {
            el.domElement.remove();
            el._targetCallout.remove();
            el._pathCallouts[0].remove();
            el._pathCallouts[1].remove();
            RenderManager.raycaster._regionTracker.removeRegion(label.object, index);
        } else {
            el._targetCallout.style.display = "none";
            el._pathCallouts[0].style.display = "none";
            el._pathCallouts[1].style.display = "none";
            el.domElement.style.display = "none";
            el.content = "";
            if (el._input) el._input.value = "";
            el.domElement.classList.remove("fadein");
        }

        if (this._meshLabels[label.object.id]) {
            const labels = this._meshLabels[label.object.id]._labels;

            if ((labels.length > 1 && index !== 0) || (labels.length === 1 && index === 0)) {
                // labels.splice(index, 1);
                labels[index] = null;

                for (let i = index; i < labels.length; i++) {
                    if (labels[i]) labels[i]._index = i;
                }
            }

            if (!labels.length || (labels.length === 1 && labels[0] === null)) { delete this._meshLabels[label.object.id]; }
        }

        this.clearSelectedRegion();
        this.update();

        fire("unselect")
    }

    toggleRegionCreation(object, intersection) {
        if (this._regionState.selected && object.uuid !== this._regionState.selected.objectUuid) this._regionState.selected = null;

        if (!this._regionState.selected) {
            const region = this.addRegion(object);
            this._regionState.selected = region;
            this._regionState.isDisplaying = true;

            const meshLabels = this._meshLabels[object.id];
            const label = meshLabels._getActiveLabel();
            label._element.setBestDistance(intersection.distance);

            if (MAX_DISTANCE * 0.75 <= intersection.distance + this.configs.clip_equation(intersection.distance)) this.alert();

            // Q3.data.quizData[label.uuid] = new QuizData();
        }

        this._drawState.isCreatingBlob = true;
        Q3.fadeoutOverlay = true;

        this.addBlobAndForget(object, intersection);

        RenderManager.enqueRaycaster();
    }

    createLabelForRegion(object, intersection) {
        if (this._regionState.selected && object.uuid !== this._regionState.selected.objectUuid) this._regionState.selected = null;

        if (!this._regionState.selected) {
            const region = this.addRegion(object);
            this._regionState.selected = region;
            this._regionState.isDisplaying = true;

            const meshLabels = this._meshLabels[object.id];
            const label = meshLabels._getActiveLabel();
            label._element.setBestDistance(intersection.distance);

            if (MAX_DISTANCE * 0.75 <= intersection.distance + this.configs.clip_equation(intersection.distance)) this.alert();

            // Q3.data.quizData[label.uuid] = new QuizData();
        }

        this.addBlob(object, intersection);
    }

    toggleDisplayRegions() {
        this._regionState.isDisplaying = !this._regionState.isDisplaying;

        if (!this._regionState.isDisplaying) {
            RenderManager.clearOverlay(this._regionState.overlay);
        } else { this.renderRegions(RenderManager.activeCamera, Q3.selected); }
    }

    resetClippingDistance(object, label, data) {
        if (!object) return;

        const camera = RenderManager.activeCamera;
        const raycaster = RenderManager.raycaster;
        const rows = raycaster.longestRows;

        /**
         * @type {MeshLabel}
         */
        const meshLabel = this._tryGetMeshLabel(object);

        meshLabel._activeRegion = data.region;

        const regionId = RenderManager.raycaster.getRegions(object)[data.region].id;

        if (!regionId) return;

        const cameraWorldPosition = camera.getWorldPosition(new THREE.Vector3());
        let distance = undefined;

        if (rows.len[regionId] > 0) {
            const vec = new THREE.Vector2(rows.x[regionId], rows.y[regionId]);
            const itsct = RenderManager.raycaster.pick(vec);


            if (itsct.objectId > 0) {
                distance = cameraWorldPosition.distanceTo(itsct.point);
            }
        }

        if (distance === undefined) {
            distance = cameraWorldPosition.distanceTo(meshLabel._object.getWorldPosition(new THREE.Vector3()));
        }

        label._element.setBestDistance(distance);

        if (MAX_DISTANCE * 0.75 <= distance + this.configs.clip_equation(distance)) this.alert();
    }

    resetGlobalRange() {
        let distanceTotal = 0, distanceElements = 0;

        const cameraWorldPosition = RenderManager.activeCamera.getWorldPosition(new THREE.Vector3());
        const tmp = new THREE.Vector3();
        const keysMeshLabels = Object.keys(this._meshLabels);

        keysMeshLabels.forEach(key => {
            const meshLabel = this._meshLabels[key];
            const obj = meshLabel._object;

            distanceTotal += cameraWorldPosition.distanceTo(obj.getWorldPosition(tmp));
            distanceElements++;

            obj.subRegions.traverse(obj => {
                if (!obj.isMesh) return;

                distanceTotal += cameraWorldPosition.distanceTo(obj.getWorldPosition(tmp));
                distanceElements++;
            });
        });

        const distance = distanceTotal / distanceElements;

        if (MAX_DISTANCE * 0.75 <= distance + this.configs.clip_equation(distance)) this.alert();

        keysMeshLabels.forEach(key => {
            this._meshLabels[key]._labels.forEach(label => {
                label._element.setBestDistance(distance);
            });
        });
    }

    setLabelData(object, label, data) {
        if (!object) return;

        /**
         * @type {MeshLabel}
         */
        const meshLabel = this._tryGetMeshLabel(object);

        meshLabel._activeRegion = data.region;

        // const label = meshLabel._getActiveLabel();

        label.fromDTO(object, data);

        this.update();

        return label.getDTO({
            regionCount: meshLabel._labels.length,
            activeRegion: meshLabel._activeRegion
        });
    }

    /**
     *
     * @param {THREE.Mesh} object
     * @param {LabelDTO} data
     */
    setMeshLabel(object, data) {
        if (!object) return;

        /**
         * @type {MeshLabel}
         */
        const meshLabel = this._tryGetMeshLabel(object);

        meshLabel._activeRegion = data.region;

        const label = meshLabel._getActiveLabel();

        label.fromDTO(object, data);

        this.update();

        return label.getDTO({
            regionCount: meshLabel._labels.length,
            activeRegion: meshLabel._activeRegion
        });
    }

    setActiveRegion(object, region) {
        if (!object) return;

        /**
         * @type {MeshLabel}
         */
        const meshLabel = this._tryGetMeshLabel(object);

        meshLabel._activeRegion = region;
    }

    getActiveRegion(object) {
        if (!object) return null;

        const meshLabel = this._meshLabels[object.id];

        if (!meshLabel) return null;

        return meshLabel._activeRegion;
    }

    getActiveLabel(object) {
        const activeRegion = this.getActiveRegion(object);

        if (activeRegion === null) return null;

        const meshLabel = this._meshLabels[object.id];

        return meshLabel._labels[activeRegion];
    }

    get blobCreationEnabled() { return this._drawState.isCreatingBlob; }

    stopBlobing() {
        this._drawState.isCreatingBlob = false;
        Q3.fadeoutOverlay = false;
        Q3.Editor.HelpTip.hide();
    }

    stopBlob() {
        if (this._modifyState.type === States.MODIFY_TYPE.MODIFY_ADD_CONTINOUS) {
            this.addBlobAndForget(this._lastState.object, this._lastState.intersection);
            return false;
        }

        if (this._modifyState.type === States.MODIFY_TYPE.MODIFY_NONE) return false;

        this._drawState.mesh = null;
        this._modifyState.type = States.MODIFY_TYPE.MODIFY_NONE;


        if (!this._regionState.isDisplaying) RenderManager.clearOverlay(this._regionState.overlay);


        RenderManager.activeCamera.update = true;

        this.clearLast();
        this.update();

        return true;
    }

    removeBlob(blob) {
        blob.parent.remove(blob);

        this.renderRegions(RenderManager.activeCamera, Q3.selected);

        this.update();
    }

    cancelBlob() {
        if (this._modifyState.type === States.MODIFY_TYPE.MODIFY_NONE) return;

        switch (this._modifyState.type) {
            case States.MODIFY_TYPE.MODIFY_MOVE:
                this._drawState.mesh.position.copy(this._modifyState.vector);
                this._drawState.mesh.quaternion.copy(this._modifyState.quaternionion);
                break;
            case States.MODIFY_TYPE.MODIFY_SCALE:
                this._drawState.mesh.scale.copy(this._modifyState.vector);
                break;
            case States.MODIFY_TYPE.MODIFY_ADD_CONTINOUS:
            case States.MODIFY_TYPE.MODIFY_ADD:
                this._drawState.mesh.parent.remove(this._drawState.mesh);
                break;
        }

        const meshLabel = this._meshLabels[this._lastState.object.id];
        let isEmpty = false;

        if (meshLabel) {
            const activeRegion = meshLabel._activeRegion;
            if (activeRegion) {
                const regionHolder = meshLabel._labels[activeRegion].object.subRegions.children[activeRegion - 1];
                const hasChildren = regionHolder.children.length > 0;
                if (!hasChildren) {
                    regionHolder.parent.remove(regionHolder);
                    meshLabel._labels.splice(activeRegion, 1);
                    isEmpty = true;
                }
            }
        }
        this._modifyState.type = States.MODIFY_TYPE.MODIFY_NONE;
        this._drawState.mesh = null;

        return isEmpty;
    }

    /**
     *
     * @param {THREE.Intersection} intersection
     */
    moveBlob(intersection) {
        const object = intersection.object;

        this._modifyState.type = States.MODIFY_TYPE.MODIFY_MOVE;
        this._modifyState.vector.copy(object.position);
        this._modifyState.quaternion.copy(object.quaternion);

        this._drawState.mesh = object;
        this._drawState.parentMatrixWorldInverse.getInverse(object.parent.region.object.matrixWorld);
        this._lastState.intersection = intersection;
    }

    /**
     *
     * @param {THREE.Intersection} intersection
     */
    modifyBlob(intersection) {
        const object = intersection.object;

        this._modifyState.type = States.MODIFY_TYPE.MODIFY_SCALE;
        this._modifyState.vector.copy(object.scale);
        object.getWorldPosition(this._drawState.beginPosition);
        this._drawState.mesh = object;
        this._drawState.parentMatrixWorldInverse.getInverse(object.parent.region.object.matrixWorld);
        this._drawState.plane
            .set(object.getWorldDirection(new THREE.Vector3()), 0)
            .translate(this._drawState.beginPosition);

        this._lastState.intersection = intersection;
    }

    /**
     * @param {THREE.Mesh} object
     * @param {THREE.Intersection} intersection
     */
    addBlobAndForget(object, intersection) {
        const meshLabel = this._meshLabels[object.id];

        if (!meshLabel) return;

        const activeRegion = meshLabel._activeRegion;

        if (activeRegion === 0) return;

        const region = meshLabel._labels[activeRegion];

        const mesh = LabelData.makeBlob(object, region);

        region._instantiated = true;

        object.updateMatrixWorld();

        const scale = this.configs.clip_equation(intersection.distance * 0.1);
        const o = LabelData.calcBlobPositionAndQuaternion({
            point: intersection.point,
            face: intersection.face,
            scale,
            object
        });

        mesh.position.copy(o.position)
        mesh.quaternion.copy(o.quaternion);
        mesh.scale.copy(o.scale);

        this._drawState.beginPosition.copy(o.position);
        this._drawState.mesh = mesh;
        this._drawState.parentMatrixWorldInverse.getInverse(object.matrixWorld);
        this._drawState.plane.set(o.normal, 0).translate(o.position);
        this._modifyState.type = States.MODIFY_TYPE.MODIFY_ADD_CONTINOUS;
        this._lastState.object = object;
        this._lastState.intersection = intersection;
    }

    hideCallouts() {
        Object.keys(this._meshLabels).forEach(key => {
            const v = this._meshLabels[key];

            v._labels.forEach(xlabel => {
                /**
                 * @type {LiveLabel}
                 */
                const label = xlabel;

                label._element.hidePaths();
            });
        });
    }


    /**
     * @param {THREE.Mesh} object
     * @param {THREE.Intersection} intersection
     */
    addBlob(object, intersection) {
        if (this._modifyState.type !== States.MODIFY_TYPE.MODIFY_NONE ||
            object !== intersection.object)
            return;

        const meshLabel = this._meshLabels[object.id];

        if (!meshLabel) return;

        const activeRegion = meshLabel._activeRegion;

        if (activeRegion === 0) return;

        const region = meshLabel._labels[activeRegion];

        const mesh = makeBlob(object, region);

        region._instantiated = true;

        object.updateMatrixWorld();

        const o = LabelData.calcBlobPositionAndQuaternion({
            point: intersection.point,
            face: intersection.face,
            scale: 1,
            object
        });

        mesh.position.copy(o.position)
        mesh.quaternion.copy(o.quaternion);

        this._drawState.beginPosition.copy(o.position);
        this._modifyState.type = States.MODIFY_TYPE.MODIFY_ADD;
        this._drawState.mesh = mesh;
        this._drawState.parentMatrixWorldInverse.getInverse(object.matrixWorld);
        this._drawState.plane.set(o.normal, 0).translate(o.position);

        this._lastState.intersection = intersection;
        this._lastState.object = object;
    }

    /**
     *
     * @param {THREE.Ray} ray
     */
    _updateBlobSize(ray) {
        const vec = new THREE.Vector3();

        if (this._modifyState.type === States.MODIFY_TYPE.MODIFY_SCALE ||
            this._modifyState.type === States.MODIFY_TYPE.MODIFY_ADD) {
            ray.intersectPlane(this._drawState.plane, vec);

            const len = this._drawState.beginPosition.distanceTo(vec);

            vec.setFromMatrixScale(this._drawState.parentMatrixWorldInverse);

            this._drawState.mesh.scale.set(len, len, len).multiply(vec);
        }
    }

    /**
     * @param {THREE.Mesh} object
     * @param {THREE.Raycaster} raycaster
     */
    _updateBlobPosition(object, raycaster) {
        // const vec = new THREE.Vector3();

        if (this._modifyState.type === States.MODIFY_TYPE.MODIFY_MOVE || this._modifyState.type === States.MODIFY_TYPE.MODIFY_ADD_CONTINOUS) {
            const intersections = [];

            let materials = ensureArray(object.material);
            if (materials[0].skinning) object.raycast(raycaster, intersections, true);
            else raycaster.intersectObject(object, true, intersections);

            const intersection = intersections[0];

            if (!intersection) return;

            const mesh = this._drawState.mesh;

            const scale = this.configs.clip_equation(intersection.distance * 0.1);
            const o = LabelData.calcBlobPositionAndQuaternion({
                point: intersection.point,
                face: intersection.face,
                scale,
                object
            });

            mesh.position.copy(o.position)
            mesh.quaternion.copy(o.quaternion);

            if (Utils.queryParams.has("blob_draw_normals")) {
                const g = new THREE.BufferGeometry();
                const p = new THREE.Float32BufferAttribute([0, 0, 0, 0, 0, 1], 3);

                g.addAttribute("position", p);

                const m = new THREE.LineBasicMaterial({ color: 0xff00ff, depthTest: false, depthWrite: false, transparent: true });
                const a = new THREE.LineSegments(g, m);

                a.position.copy(mesh.position);
                a.quaternion.copy(mesh.quaternion);
                mesh.parent.add(a);

                setTimeout(function () {
                    a.parent.remove(a);
                    g.dispose();
                    m.dispose();
                }, 1000);
            }

            if (this._modifyState.type === States.MODIFY_TYPE.MODIFY_ADD_CONTINOUS) {
                mesh.scale.copy(o.scale);
            }
        }

    }

    /**
     * @param {THREE.Mesh} object
     * @param {THREE.Raycaster} raycaster
     */
    updateBlob(object, raycaster) {
        switch (this._modifyState.type) {
            case States.MODIFY_TYPE.MODIFY_ADD_CONTINOUS:
            case States.MODIFY_TYPE.MODIFY_MOVE:
                this._updateBlobPosition(object, raycaster);
                break;
            case States.MODIFY_TYPE.MODIFY_NONE:
            case States.MODIFY_TYPE.MODIFY_SCALE:
            default:
                this._updateBlobSize(raycaster.ray);
                break;
        }
    }

    renderRegions(camera, object, noHighlight = false) {
        if (object && object.isLivelabel) object = object.object;
        if (!object || !this._meshLabels[object.id]) return;
        if (!object.isMesh) return;

        RenderManager.renderOverlay(this._regionState.overlay, { object });
        RenderManager.activeCamera.update = true;
    }

    getLabelForRegion(regionId) {
        const region = RenderManager.raycaster.getRegionById(regionId);

        if (!region) return null;

        const { object, subId } = region;

        const meshLabel = this._meshLabels[object.id];

        if (!meshLabel) return null;

        const label = meshLabel._labels[subId + 1];

        return label || null;
    }

    setHighlightLabel(label, isHighlighted, is2D) {
        if (this._highlightState.highlighted) {
            if (!is2D && this._highlightState.isHighlight2d) return;

            this._highlightState.highlighted._setHighlighted(false);
        }

        this._highlightState.highlighted = isHighlighted ? label : null;
        this._highlightState.isHighlight2d = is2D;

        label._setHighlighted(isHighlighted);

        this.update();
    }

    serialize() {
        return Object.keys(this._meshLabels).map(objectId => {
            /**
             * @type {MeshLabel}
             */
            const meshLabel = this._meshLabels[objectId];
            const labels = meshLabel._labels || [];
            const object = meshLabel._object;

            if (labels.length === 0) {
                console.error("Something in tour broke live label structure.");
            }

            const filtered = labels.filter((label, i) => {
                const subMeshIndex = i - 1;
                const subs = object.subRegions.children[subMeshIndex];

                if (!label || subMeshIndex >= 0 && (!subs || subs.children.length === 0))
                    return false;

                return label._element.content && label._element.content.length > 0;
            });

            return {
                object_uuid: object.uuid,
                label: filtered.map(label => label && label.serialize(object))
            };
        });
    }


    /**
     *
     * @param {string} version
     * @param {THREE.Mesh} object
     * @param {any[]} data
     */
    deserialize(version, object, data) {
        // const isSupportedVersion = SUPPORTED_VERSIONS.some(a => a === version);
        //
        // if (!isSupportedVersion) throw new Error("Live labels were saved with unsupported version.");

        if (!object || !data || data.length === 0) return;

        const meshLabel = this._tryGetMeshLabel(object);
        let subRegion = 1;

        data.forEach(label => {
            // this.addLabel(object, meshLabel, label, label.index === 0 ? 0 : subRegion++)
            if (label.index === 0) { meshLabel._labels[0]._element.uuid = label.uuid; meshLabel._labels[0]._element.domElement.style.removeProperty("display") } else {
                if (RenderManager.raycaster.getRegionCount(object) - 1 < subRegion) {
                    meshLabel._addRegion(label.uuid);
                    RenderManager.raycaster.addRegion(object);
                    label.index = subRegion++;
                }
            }

            label.region = meshLabel._activeRegion = label.index;

            const newLabel = meshLabel._getActiveLabel();
            newLabel.deserialize(object, label);

            newLabel._element.uuid = label.uuid;

        });

        const regions = RenderManager.raycaster.getRegions(object);
        for (let region of regions) {
            const meshLabel = this._meshLabels[object.id];
            if (meshLabel) {
                const { subId } = region;
                const label = meshLabel._labels[subId + 1];
                if (label._isValid) {
                    const regionId = region.id;
                    this._layoutLabelActivator.enableLabel(regionId, true);
                }
            }
        }

    }

    addLabel(object, meshLabel, label, index) {
        if (label.index === 0) {
            if (meshLabel._labels[0]) {
                meshLabel._labels[0]._element.uuid = label.uuid;
                meshLabel._labels[0]._element.domElement.style.removeProperty("display")
            } else {
                this.initMeshLabel(object);

            }
        } else if (RenderManager.raycaster.getRegionCount(object) - 1 < index) {
            meshLabel._addRegion(label.uuid);
            RenderManager.raycaster.addRegion(object);
            label.index = index;
        }

        label.region = meshLabel._activeRegion = label.index;
        const newLabel = meshLabel._getActiveLabel();
        newLabel.deserialize(object, label);

        newLabel._element.uuid = label.uuid;
    }

    undeleteLabel(object, labelData) {
        const meshLabel = this._tryGetMeshLabel(object);
        const index = RenderManager.raycaster.getRegionCount(object);
        this.addLabel(object, meshLabel, labelData, index);
    }
}

export default LivelabelManager;
export { LivelabelManager };

let assetInfoBox, assetInfoBoxSize;

function collectBoxes(boxes, w, h) {
    if (Q3.isAssetMode && Q3.assetMode === "view") {
        assetInfoBox = assetInfoBox || document.getElementById("asset-viewer-details");

        if (assetInfoBox) {
            assetInfoBoxSize = assetInfoBox.getBoundingClientRect();
            const pr = RenderManager.pixelRatio;

            const bbox = [
                assetInfoBoxSize.x, assetInfoBoxSize.y,
                assetInfoBoxSize.x + assetInfoBoxSize.width, assetInfoBoxSize.y + assetInfoBoxSize.height
            ].map(x => x * pr);

            bbox.isElement2d = true;

            boxes.push(bbox);

        }
    }

    LayersManager.graph.forEach(layer => {
        function callback(child) {
            if (child.domElement && child.domElement.parentNode) {
                child.objects.forEach(object => {
                    const layer = Q3.data.layers.layers2D[object.parentLayer.uuid];

                    if (!layer) return;

                    const isObstructingElements = layer.objects[object.uuid].hideLivelabels;

                    if (!isObstructingElements || !object.getBBox) return;

                    const bbox = object.getBBox(w, h);

                    bbox.isElement2d = true;
                    boxes.push(bbox);
                });
            }
        }

        switch (layer.type) {
            case "2DGroup":
                layer.children.forEach(callback);
                break;
            case "2D":
                layer.children.forEach(callback);
                callback(layer);
                break;
        }
    });
}

function filterBoxes(boxes) {
    const filteredOut = {};

    BoxIntersect(boxes, (a, b) => {
        if (boxes[a].isElement2d) { filteredOut[b] = a; }
        else if (boxes[a].isElement2d) { filteredOut[a] = b; }
        else if (!filteredOut[b] && !filteredOut[a]) { filteredOut[a] = b; }
    });

    return filteredOut;
}